import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonComponent } from 'app/shared/components/button/button.component';
import { DS_ICONS } from 'app/shared/components/icons/ds-icons.constants';
import { IconComponent } from 'app/shared/components/icons/icon/icon.component';
import { SkeletonDirective } from 'app/shared/components/skeleton/skeleton.directive';

import { NotificationType } from '../../models/notification-view';
import { notificationMessageKey } from '../../presentation/notification-mapper';

const ICONS = {
    info: DS_ICONS.info,
    reminder: DS_ICONS.notification,
} as const;

type NotificationConfig = {
    icon: keyof typeof ICONS;
    actionKey?: string;
};

const NOTIFICATION_TYPES = {
    linkRequest: { icon: 'info', actionKey: 'NOTIFICATION_ITEM_VIEW_INSTALLATION' },
    rejectedLinkRequest: { icon: 'info' },
    deletedLinkRequest: { icon: 'info' },
    admin: { icon: 'info', actionKey: 'NOTIFICATION_ITEM_VIEW' },
    alertReminder: { icon: 'reminder', actionKey: 'NOTIFICATION_ITEM_ALERT_ACTION' },
    linkRequestReminder: { icon: 'reminder', actionKey: 'NOTIFICATION_ITEM_LINK_REQUEST_RESEND_REQUEST' },
    interventionExpired: { icon: 'reminder', actionKey: 'NOTIFICATION_ITEM_INTERVENTION_EXPIRED_ACTION' },
} as const satisfies Record<NotificationType, NotificationConfig>;

const MIN_LABEL_SKELETON_WIDTH = 45;
const MAX_LABEL_SKELETON_WIDTH = 90;

const randomLabelSkeletonWidth = (): string => {
    const span = MAX_LABEL_SKELETON_WIDTH - MIN_LABEL_SKELETON_WIDTH;
    return `${MIN_LABEL_SKELETON_WIDTH + Math.floor(Math.random() * (span + 1))}%`;
};

@Component({
    selector: 'app-notification-item',
    templateUrl: './notification-item.component.html',
    styleUrl: './notification-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent, ButtonComponent, TranslatePipe, SkeletonDirective],
})
export class NotificationItemComponent {
    readonly type = input.required<NotificationType>();
    readonly params = input<Record<string, unknown>>({});
    readonly message = input<string | undefined>();
    readonly actionKey = input<string | null | undefined>();
    readonly loading = input(false);

    readonly action = output<void>();

    protected readonly skeletonLabelWidth = randomLabelSkeletonWidth();

    protected readonly config = computed<NotificationConfig | null>(() =>
        this.loading() ? null : NOTIFICATION_TYPES[this.type()],
    );
    protected readonly iconPath = computed(() => {
        const config = this.config();
        return config ? ICONS[config.icon] : '';
    });
    protected readonly resolvedMessageKey = computed(() =>
        this.loading() ? undefined : notificationMessageKey(this.type(), this.params()),
    );
    protected readonly showMessage = computed(() => Boolean(this.message() || this.resolvedMessageKey()));
    protected readonly resolvedActionKey = computed(() =>
        this.actionKey() === undefined ? this.config()?.actionKey : this.actionKey(),
    );
}
